import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import Link from 'next/link';
import { Spinner } from '../../Spinner';
import { useBasePath } from '../../../lib/use-base-path';
import type { ClickModifiers } from '../../../lib/use-in-app-link';
import styles from './Button.module.css';
import skins from './buttonSkins.module.css';

// THE button for the whole app — Figma "⚠️ Symphony DS" → Button (node 168:7692).
// Never hand-roll a <button>; see CLAUDE.md → "Buttons".
//
// The design is Skin × Type × State × Size. Prop names stay MUI-flavoured per the
// design-system convention, so:
//   variant → Figma *Type*  (contained = Primary, outlined = Secondary, text = Ghost)
//   color   → Figma *Skin*  (standard | accent | destructive)
// Every state colour comes from the `Fill (buttons)` / `Border` token groups, so a
// new skin is a token lookup rather than a design decision.
//
// Polymorphic: pass `href` and it renders a same-origin Next <Link> (with the
// active mount prefix) instead of a <button>, so a CTA-as-link shares one look.
export type ButtonVariant = 'contained' | 'outlined' | 'text';
export type ButtonSize = 'tiny' | 'small' | 'medium' | 'large';
// `light` (Figma Skin=Light — a solid-white primary) and `waiting` (the orange
// waiting-on-you tint) are CONTAINED-ONLY skins: the agents-island quick-view's
// "See more" (white) and "needs your action" (orange) CTAs. They carry no
// outlined/text form, so they sit outside the nine-combo Skin×Type matrix.
export type ButtonColor = 'standard' | 'accent' | 'destructive' | 'light' | 'waiting';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  color?: ButtonColor;
  fullWidth?: boolean;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  children?: ReactNode;
  className?: string;
}

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & {
    href?: undefined;
  };

type ButtonAsLink = CommonProps & {
  href: string;
  /** Skip the mount-prefix rewrite for external / already-absolute URLs. */
  external?: boolean;
  /** Override the Next <Link> `as` path (default: the active mount + href). */
  linkAs?: string;
  /** Forwarded to the Next <Link>; ignored when `external`. */
  prefetch?: boolean;
  target?: string;
  rel?: string;
  // Takes the click's modifier fields, not `() => void`: a modified click (cmd/ctrl/shift/alt,
  // middle-click) still calls this — the anchor's real `href` is what lets it open in a new tab — so
  // a caller that only leaves-the-page/collapses-chat on a genuine same-tab navigation needs a way to
  // tell the two apart. `ClickModifiers` rather than `MouseEvent<HTMLAnchorElement>` so the SAME
  // `onClick` value can be reused (as `CreditsWidgetCtaButton` does) when this same prop renders as a
  // native `<button>` instead — see `routerHandlesClick`.
  onClick?: (event?: ClickModifiers) => void;
  'aria-label'?: string;
};

export type ButtonProps = ButtonAsButton | ButtonAsLink;

function classesFor(props: CommonProps) {
  const { variant = 'contained', size = 'medium', color = 'standard', fullWidth, loading, className } = props;
  return [
    styles.root,
    styles[`size-${size}`],
    // The Skin × Type colours are shared with IconButton — see buttonSkins.module.css.
    skins[`${variant}-${color}`],
    fullWidth && styles.fullWidth,
    loading && styles.loading,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

function Inner({ loading, startIcon, endIcon, children }: CommonProps) {
  return (
    <>
      {loading ? <Spinner /> : startIcon}
      {children != null && <span className={styles.label}>{children}</span>}
      {!loading && endIcon}
    </>
  );
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(props, ref) {
  const basePath = useBasePath();

  if (props.href !== undefined) {
    const {
      href,
      external,
      linkAs,
      prefetch,
      variant,
      size,
      color,
      fullWidth,
      loading,
      startIcon,
      endIcon,
      children,
      className,
      ...rest
    } = props;
    const cls = classesFor(props);
    if (external) {
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={cls} {...rest}>
          <Inner loading={loading} startIcon={startIcon} endIcon={endIcon}>
            {children}
          </Inner>
        </a>
      );
    }
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        as={linkAs ?? `${basePath}${href}`}
        prefetch={prefetch}
        className={cls}
        {...rest}
      >
        <Inner loading={loading} startIcon={startIcon} endIcon={endIcon}>
          {children}
        </Inner>
      </Link>
    );
  }

  const { variant, size, color, fullWidth, loading, startIcon, endIcon, children, className, disabled, type, ...rest } =
    props;
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type ?? 'button'}
      className={classesFor(props)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <Inner loading={loading} startIcon={startIcon} endIcon={endIcon}>
        {children}
      </Inner>
    </button>
  );
});
